import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { SegmentedMeter } from "@msuite/ui/segmented-meter";
import { Tag } from "@msuite/ui/tag";
import type { IncomingInvoiceDetail, Nullable } from "@msuite/utils/types";
import { getPayableAmounts } from "@msuite/utils/incoming-invoices";
import { formatInvoiceAmount } from "@/components/incoming-invoices";

/** Props Interface */
interface CostAllocationSummaryProps {
	incomingInvoice: Nullable<IncomingInvoiceDetail>;
}

/** The net amount, how much of it is allocated, what is left, and who made the split. */
export function CostAllocationSummary({ incomingInvoice }: CostAllocationSummaryProps) {
	/** Context */
	const { t } = useTranslation();

	/** Computed */
	const currency = incomingInvoice?.currency ?? "EUR";
	const allocations = incomingInvoice?.costAllocations ?? [];
	// The payable net amount: the reduced one once an approver applied a Kürzung.
	const netAmount = incomingInvoice ? getPayableAmounts(incomingInvoice).netAmount : null;
	let allocatedCents = 0;
	for (const allocation of allocations) allocatedCents += Math.round(allocation.amount * 100);
	const remaining =
		netAmount === null ? null : (Math.round(netAmount * 100) - allocatedCents) / 100;
	const source = allocations[0]?.source ?? null;
	// The meter splits by cost centre, so a split across three reads as three parts of the bar.
	const segments = allocations.map((allocation) => ({
		id: allocation.id,
		label: `${allocation.costCenter.number} – ${allocation.costCenter.name}`,
		value: allocation.amount,
		color: allocation.costCenter.color,
	}));

	/** Render */
	return (
		<ContentCard
			title={t("incoming_invoices.cost_allocation")}
			icon={icon("Split")}
		>
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.net_amount")}</DataGrid.Label>
					<DataGrid.Value>{formatInvoiceAmount(netAmount, currency)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.allocated")}</DataGrid.Label>
					<DataGrid.Value>{formatInvoiceAmount(allocatedCents / 100, currency)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.not_allocated")}</DataGrid.Label>
					<DataGrid.Value className={remaining ? "text-rose-500" : undefined}>
						{formatInvoiceAmount(remaining, currency)}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.allocated_by")}</DataGrid.Label>
					<DataGrid.Value>
						{source ? (
							<Tag
								color={source === "suggested" ? "violet" : "blue"}
								variant="plain"
							>
								{t(
									source === "suggested"
										? "incoming_invoices.allocation_source.suggested"
										: "incoming_invoices.allocation_source.manual",
								)}
							</Tag>
						) : (
							t("incoming_invoices.not_allocated_yet")
						)}
					</DataGrid.Value>
				</DataGrid.Row>
			</DataGrid>
			<div className="pt-4">
				<SegmentedMeter
					segments={segments}
					total={netAmount ?? 0}
					remainderLabel={t("incoming_invoices.not_allocated")}
				/>
			</div>
		</ContentCard>
	);
}
